import { useMemo } from 'react';
import { SelectOption } from 'global';
import { useQuery } from '@apollo/client';

import { InventoryOptionEquipmentAttribute } from '../types';
import { GET_INVENTORY_OPTION_EQUIPMENT_ATTRIBUTES } from '../api';

interface Selection {
  year?: string | null;
  make?: string | null;
  model?: string | null;
  modelBase?: string | null;
}

interface AttributesResponse {
  inventoryOptionEquipmentAttributes: { attributes: InventoryOptionEquipmentAttribute[] };
}

const toOptions = (values: Array<string | null | undefined>): SelectOption<string>[] =>
  Array.from(new Set(values.filter((value): value is string => !!value && value.trim().length > 0)))
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({ label: value, value }));

export const useEquipmentAttributes = (accountId: string) => {
  const { data, loading } = useQuery<AttributesResponse>(GET_INVENTORY_OPTION_EQUIPMENT_ATTRIBUTES, {
    variables: { accountId },
    skip: !accountId,
  });

  const attributes = useMemo(() => data?.inventoryOptionEquipmentAttributes?.attributes ?? [], [data]);

  const getYearOptions = () => toOptions(attributes.map(attribute => attribute.year));

  const getMakeOptions = () => toOptions(attributes.map(attribute => attribute.make));

  const getModelOptions = ({ make }: Selection) =>
    toOptions(attributes.filter(attribute => !make || attribute.make === make).map(attribute => attribute.model));

  const getModelBaseOptions = ({ make, model }: Selection) =>
    toOptions(
      attributes
        .filter(attribute => (!make || attribute.make === make) && (!model || attribute.model === model))
        .map(attribute => attribute.modelBase)
    );

  return { loading, attributes, getYearOptions, getMakeOptions, getModelOptions, getModelBaseOptions };
};
